"use client";

import * as React from "react";
import {
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselWrapper,
  type CarouselWrapperProps,
} from "@/components/ui/carousel/carouselItems";
import { cva } from "class-variance-authority";

interface CarouselProps extends CarouselWrapperProps {
  /**
   * Array of items to render in the carousel
   */
  items: React.ReactNode[];
  /**
   * Whether to show navigation arrows
   * @default true
   */
  showArrows?: boolean;
  /**
   * Additional class names for the carousel container
   */
  className?: string;
  /**
   * Additional class names for each carousel item
   */
  itemClassName?: string;
  /**
   * Whether the carousel should allow items to be visible outside of its viewport
   * @default false
   */
  overflowVisible?: boolean;
  /**
   * Visual theme of the carousel controls
   * @default 'dark'
   */
  variant?: "light" | "dark";
  /**
   * Whether the navigation buttons should be constrained to a container width
   * @default false
   */
  fullWidth?: boolean;

  arrowPosition?: "outside" | "bottom";
}

const CarouselVariants = cva("relative w-full flex md:block flex-col");

/**
 * A generic carousel component that wraps shadcn/ui carousel.
 * Features:
 * - Accepts an array of React components as items
 * - Optional arrows positioned at the bottom left
 * - Fully customizable via shadcn/ui carousel props (opts, plugins, etc.)
 */
export const Carousel: React.FC<CarouselProps> = ({
  items,
  showArrows = true,
  fullWidth = false,
  arrowPosition = "outside",
  className,
  itemClassName,
  overflowVisible = false,
  variant = "dark",
  ...props
}) => {
  return (
    <CarouselWrapper
      className={CarouselVariants({ className })}
      overflowVisible={overflowVisible}
      variant={variant}
      {...props}
    >
      <CarouselContent>
        {items.map((item, index) => (
          <CarouselItem key={index} className={itemClassName}>
            {item}
          </CarouselItem>
        ))}
      </CarouselContent>

      {arrowPosition === "outside" && showArrows && !fullWidth && (
        <div className="mt-md flex gap-sm">
          <CarouselPrevious className="" />
          <CarouselNext className="" />
        </div>
      )}

      {arrowPosition === "outside" && showArrows && fullWidth && (
        <div className="self-center">
          <CarouselPrevious className="md:absolute left-xl top-1/2 -translate-y-1/2 z-10 mr-4 md:mr-0 xl:left-2xl 2xl:left-3xl" />
          <CarouselNext className="md:absolute right-xl top-1/2 -translate-y-1/2 z-10 xl:right-2xl 2xl:right-3xl" />
        </div>
      )}

      {arrowPosition === "bottom" && showArrows && (
        <div className="relative mt-sm md:mt-md flex gap-sm md:gap-md">
          <CarouselPrevious className="md:static md:top-auto! md:translate-y-0!" />
          <CarouselNext className="md:static md:top-auto! md:translate-y-0!" />
        </div>
      )}
    </CarouselWrapper>
  );
};
